import { useForm } from '@tanstack/react-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';

import { Button } from '@/components/button/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog';
import { Input } from '@/components/input/input';
import { Label } from '@/components/label/label';
import Select from '@/components/select/select';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import { GET_LIST_ADMIN_QUERY_KEY } from '@/features/admins/api/get-list-admin.api';
import {
  GET_ADMIN_QUERY_KEY,
  getAdminQueryOptions,
} from '@/features/admins/api/get-admin.api';
import { updateAdminMutationOptions } from '@/features/admins/api/update-admin.api';
import { getListRoleQueryOptions } from '@/features/roles/api/get-list-role.api';

export interface EditAdminDialogProps extends BaseDialogProps {
  adminId: string;
}

export default function EditAdminDialog({
  dialog,
  adminId,
}: EditAdminDialogProps) {
  const { closeEditAdminDialog } = useV2DialogContext();
  const queryClient = useQueryClient();

  const getAdminQuery = useQuery(getAdminQueryOptions(adminId));
  const admin = getAdminQuery.data?.result;

  const getListRoleQuery = useQuery(getListRoleQueryOptions({ limit: 100 }));
  const roleOptions = (getListRoleQuery.data?.result.data ?? []).map(role => ({
    value: role.id,
    label: role.role,
  }));
  const isLoadingRoles = getListRoleQuery.isPending;

  const updateAdminMutation = useMutation({
    ...updateAdminMutationOptions(),
    onSuccess: () => {
      toast.success('Berhasil mengubah data admin');
      queryClient.invalidateQueries({ queryKey: [GET_LIST_ADMIN_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [GET_ADMIN_QUERY_KEY, adminId],
      });
      closeEditAdminDialog();
    },
    onError: error => {
      const message =
        error instanceof AxiosError
          ? ((error.response?.data as { message?: string })?.message ??
            error.message)
          : 'Gagal mengubah data admin';
      toast.error(message);
    },
  });

  const form = useForm({
    defaultValues: {
      name: admin?.admin_name ?? '',
      email: admin?.admin_email ?? '',
      role_id: admin?.role_id ?? '',
      password: '',
      password_confirmed: '',
    },
    onSubmit: async ({ value }) => {
      if (value.password !== value.password_confirmed) {
        toast.error('Konfirmasi password tidak cocok');
        return;
      }
      await updateAdminMutation.mutateAsync({
        id: adminId,
        data: {
          name: value.name,
          email: value.email,
          role_id: value.role_id || undefined,
          password: value.password || undefined,
          password_confirmed: value.password_confirmed || undefined,
        },
      });
    },
  });

  const isLoading = getAdminQuery.isPending;
  const isPending = updateAdminMutation.isPending;

  return (
    <Dialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState) {
          closeEditAdminDialog();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Admin</DialogTitle>
          <DialogDescription>
            Perbarui data admin di bawah ini.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex min-h-40 items-center justify-center text-sm text-muted-foreground">
            Memuat data admin...
          </div>
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={e => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <form.Field name="name">
              {field => (
                <div className="grid gap-2">
                  <Label htmlFor={field.name}>Nama Admin</Label>
                  <Input
                    id={field.name}
                    placeholder="Masukkan Nama Admin"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="email">
              {field => (
                <div className="grid gap-2">
                  <Label htmlFor={field.name}>Email</Label>
                  <Input
                    id={field.name}
                    type="email"
                    placeholder="Masukkan Email"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="role_id">
              {field => (
                <div className="grid gap-2">
                  <Label>Role</Label>
                  <Select
                    className="w-full"
                    placeholder={
                      isLoadingRoles ? 'Memuat role...' : 'Pilih Role'
                    }
                    options={roleOptions}
                    value={field.state.value}
                    onValueChange={value => field.handleChange(value)}
                    disabled={isLoadingRoles}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="password">
              {field => (
                <div className="grid gap-2">
                  <Label htmlFor={field.name}>Password</Label>
                  <Input
                    id={field.name}
                    type="password"
                    placeholder="Kosongkan jika tidak diubah"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="password_confirmed">
              {field => (
                <div className="grid gap-2">
                  <Label htmlFor={field.name}>Konfirmasi Password</Label>
                  <Input
                    id={field.name}
                    type="password"
                    placeholder="Masukkan Konfirmasi Password"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={closeEditAdminDialog}
                disabled={isPending}
              >
                Batal
              </Button>
              <form.Subscribe selector={state => [state.canSubmit]}>
                {([canSubmit]) => (
                  <Button type="submit" disabled={!canSubmit || isPending}>
                    {isPending ? 'Menyimpan...' : 'Simpan'}
                  </Button>
                )}
              </form.Subscribe>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
